/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProactiveChatNames } from '../../helpers/consts';
import { IChatContext, TContextProvider } from '../../../interfaces';
import { IPvaChatConnectorViewProps } from '../../msdyn365-pva-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * WaitOnTimeTrigger - proactive chat is triggered if user stays on current page for more than (config.proactiveOnWaitTimeSeconds) seconds
 */
class WaitOnTimeTrigger extends ProactiveChatTrigger {
    private waitTimeInMilliseconds: number | undefined;

    constructor(props: IPvaChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        if (this.props.config.proactiveOnWaitTimeSeconds && !isNaN(this.props.config.proactiveOnWaitTimeSeconds)) {
            this.waitTimeInMilliseconds = Number(this.props.config.proactiveOnWaitTimeSeconds) * 1000;
            this.props.context.telemetry.debug(
                `[Chat][${WaitOnTimeTrigger.name}] Wait time (ms) set to: '${this.waitTimeInMilliseconds}'.`
            );
        }
    }

    public async validate(): Promise<boolean> {
        const retVal: boolean = this.waitTimeInMilliseconds !== undefined;
        this.props.context.telemetry.debug(`[Chat][${WaitOnTimeTrigger.name}] Validation returning '${retVal}'.`);
        return retVal;
    }

    public getContextProvider(): TContextProvider {
        const triggerTime = Date.now() + (this.waitTimeInMilliseconds || 0);
        return () => {
            return triggerTime > Date.now()
                ? this.context
                : {
                      ...this.context,
                      eCommerceCustIsProactiveChat: 'True',
                      eCommerceCustProactiveType: ProactiveChatNames.WaitOnTime,
                      eCommerceCustProactiveTimeOnPage: `${this.waitTimeInMilliseconds ? this.waitTimeInMilliseconds : 0}`
                  };
        };
    }

    public getMessage(): string {
        // @TODO: Add message processing if needed
        return this.props.config.proactiveOnWaitTimeMessage || this.props.config.proactiveDefaultMessage;
    }
}

export default WaitOnTimeTrigger;
